import { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';
import { GLASS_SURFACES } from '../../config/glassSurfaces';

/**
 * Canonical Prii button.
 *
 * Usage:
 *   <Button>Save</Button>                              // primary, md
 *   <Button variant="secondary">Cancel</Button>
 *   <Button variant="destructive" size="sm">Delete</Button>
 *   <Button variant="ghost">Skip</Button>
 *   <Button variant="icon" aria-label="Close"><X /></Button>
 *   <Button variant="icon" selected aria-label="Favourited"><Heart /></Button>
 *   <Button as="a" href="/about">Learn more</Button>
 *   <Button loading>Saving...</Button>
 *
 * Props:
 *   variant   - 'primary' | 'chipSelected' | 'secondary' | 'ghost' | 'destructive' | 'icon'
 *               chipSelected: selected filter/toggle chips (accent glass, static glow)
 *   size      - 'sm' | 'md' | 'lg' | 'xl'  (default 'md'; xl is icon-only, 56px)
 *   loading   - boolean - shows spinner, disables button
 *   disabled  - standard
 *   selected  - icon variant only — accent glass when true
 *   as        - 'button' | 'a' | React component (e.g. Link). Pass href/to for links.
 *   fullWidth - boolean - sets w-full
 *   leftIcon  - optional ReactNode rendered before children
 *   rightIcon - optional ReactNode rendered after children
 *   className - merged in last for overrides; avoid using to fight the variant
 *   children  - label content
 *
 * All other props pass through (onClick, type, form, href, target, aria-*).
 */
const BASE =
  'inline-flex items-center justify-center gap-2 rounded-btn font-semibold ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-modal-bg ' +
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none ' +
  'select-none whitespace-nowrap';

const PRIMARY_ACTION = 'btn-glass-accent btn-glass-accent--breathe disabled:saturate-75';

const CHIP_SELECTED = 'btn-glass-accent';

const SECONDARY_GLASS = 'btn-glass-neutral';
const SECONDARY_SOLID = 'btn-secondary-solid transition-colors';

const VARIANTS = {
  primary: PRIMARY_ACTION,
  chipSelected: CHIP_SELECTED,
  secondary: GLASS_SURFACES.secondaryButtons ? SECONDARY_GLASS : SECONDARY_SOLID,
  ghost: 'btn-ghost-glass',
  destructive: 'btn-glass-destructive',
  icon: 'btn-glass-icon btn-glass-icon-neutral',
};

const ICON_SELECTED = 'btn-glass-icon btn-glass-icon-accent';

const SIZES = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
};

const ICON_SIZES = {
  sm: 'h-8 w-8 shrink-0 p-1.5',
  md: 'h-10 w-10 shrink-0 p-2',
  lg: 'h-12 w-12 shrink-0 p-2.5',
  xl: 'h-14 w-14 shrink-0 p-3.5',
};

function joinClasses(...parts) {
  return parts.filter(Boolean).join(' ');
}

const Button = forwardRef(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    disabled = false,
    selected = false,
    as,
    href,
    fullWidth = false,
    leftIcon,
    rightIcon,
    className = '',
    children,
    type,
    ...rest
  },
  ref,
) {
  const Tag = as ?? (href ? 'a' : 'button');
  const isNativeButton = Tag === 'button';
  const isNativeAnchor = Tag === 'a';

  const variantClass =
    variant === 'icon' && selected ? ICON_SELECTED : (VARIANTS[variant] ?? VARIANTS.primary);

  const sizeClass =
    variant === 'icon' ? (ICON_SIZES[size] ?? ICON_SIZES.md) : (SIZES[size] ?? SIZES.md);

  const composed = joinClasses(
    BASE,
    variant === 'icon' && '!rounded-full',
    variantClass,
    sizeClass,
    fullWidth && 'w-full',
    className,
  );

  const buttonType = isNativeButton ? (type ?? 'button') : undefined;

  const isDisabled = disabled || loading;

  const content = loading ? (
    <>
      <Loader2 className="animate-spin" size={size === 'lg' ? 20 : 16} aria-hidden />
      <span className="sr-only">Loading…</span>
      {variant !== 'icon' && children}
    </>
  ) : (
    <>
      {leftIcon ? <span className="shrink-0">{leftIcon}</span> : null}
      {children}
      {rightIcon ? <span className="shrink-0">{rightIcon}</span> : null}
    </>
  );

  return (
    <Tag
      ref={ref}
      className={composed}
      type={buttonType}
      href={isNativeAnchor ? href : undefined}
      aria-busy={loading || undefined}
      aria-disabled={isDisabled || undefined}
      aria-pressed={variant === 'icon' && selected ? true : undefined}
      disabled={isNativeButton && isDisabled ? true : undefined}
      {...rest}
    >
      {content}
    </Tag>
  );
});

export default Button;
